/**
 * Simple CSV Template Generator
 * Creates clean, minimal CSV templates without complex validation
 */

// Minimal CSV parser that supports quoted fields (RFC4180-ish).
const parseCsvLine = (line) => {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out.map((v) => v.trim());
};

const csvEscape = (value) => {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const parseCsvContent = (csvContent) => {
  const raw = csvContent
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '');

  if (raw.length === 0) return { headers: [], rows: [] };
  const headers = parseCsvLine(raw[0]);
  const rows = raw.slice(1).map(parseCsvLine);
  return { headers, rows };
};

// Maps every display column header to the backend field name
export const HEADER_TO_FIELD = {
  'id': 'id',
  'First name': 'firstname',
  'Last name': 'lastname',
  'Full name as QID': 'full_name',
  'QID number': 'id_number',
  'QID Expiry Date': 'id_expiry_date',
  'IBAN number': 'iban',
  'Email': 'email',
  'Address': 'address',
  'Phone Number': 'phone',
  'Institution': 'institution',
  'Rank': 'rank',
  'Rotation type': 'speciality',
  'Nationality': 'nationality',
  // Legacy internal names — accepted if user uploads an old template
  'firstname': 'firstname',
  'lastname': 'lastname',
  'email': 'email',
  'address': 'address',
  'phone': 'phone',
  'status': 'status',
  'rank': 'rank',
  'speciality': 'speciality',
  'institution': 'institution',
  'isAdmin': 'isAdmin',
  'full_name': 'full_name',
  'id_number': 'id_number',
  'id_expiry_date': 'id_expiry_date',
  'iban': 'iban',
  'nationality': 'nationality',
};

// Template configurations — displayHeaders are what appear in the downloaded CSV file
export const TEMPLATE_CONFIGS = {
  students: {
    fields: ['id', 'firstname', 'lastname', 'rank', 'email', 'phone', 'address', 'status', 'nationality'],
    displayHeaders: ['id', 'firstname', 'lastname', 'rank', 'email', 'phone', 'address', 'status', 'Nationality'],
    filename: 'student_import_template.csv',
    title: 'Student Import Template',
    examples: [
      ['', 'Amina', 'Khalid', 'pharmd', 'amina.khalid@qu.edu.qa', '', 'Doha Campus Road', 'active', 'Qatar'],
      ['123', 'Sarah', 'Johnson', 'spep', 'sarah.johnson@qu.edu.qa', '+97412345678', '', 'active', 'Palestine'],
      ['', 'Ahmed', 'Rashid', 'both', 'ahmed.rashid@qu.edu.qa', '', 'Medical Campus Doha', 'inactive', '']
    ]
  },
  preceptors: {
    fields: ['id', 'firstname', 'lastname', 'full_name', 'id_number', 'id_expiry_date', 'iban', 'email', 'phone', 'institution', 'rank', 'speciality', 'nationality'],
    displayHeaders: ['id', 'First name', 'Last name', 'Full name as QID', 'QID number', 'QID Expiry Date', 'IBAN number', 'Email', 'Phone Number', 'Institution', 'Rank', 'Rotation type', 'Nationality'],
    filename: 'preceptor_import_template.csv',
    title: 'Preceptor Import Template',
    examples: [
      ['', 'Mariam', 'Noor', 'Mariam Ahmed Noor', '12345678901', '31/12/25', 'QA58DOHB00001234567890ABCDEFG', 'mariam.noor@hamad.qa', '+97412345678', 'Hamad Medical Corporation', 'pharmd', 'Clinical Pharmacy', 'Qatar'],
      ['321', 'Fatima', 'Zahra', 'Fatima Ali Zahra', '98765432100', '30/06/26', 'QA58SIDRA0001234567890ABCDEFG', 'fatima.zahra@sidra.org', '+97450000000', 'Sidra Medicine', 'spep', 'Internal Medicine', 'Egypt']
    ]
  },
  admins: {
    fields: ['id', 'firstname', 'lastname', 'email', 'address', 'phone', 'nationality'],
    displayHeaders: ['id', 'First name', 'Last name', 'Email', 'Address', 'Phone Number', 'Nationality'],
    filename: 'admin_import_template.csv',
    title: 'Administrator Import Template',
    examples: [
      ['', 'Nora', 'Hassan', 'nora.hassan@qu.edu.qa', 'University Campus Doha', '', 'Qatar'],
      ['', 'Samir', 'Mahmoud', 'samir.mahmoud@qu.edu.qa', '', '', 'Sudan'],
      ['', 'Lina', 'Karim', 'lina.karim@qu.edu.qa', 'Administration Building Doha', '', '']
    ]
  }
};

// Field descriptions and validation info (simplified)
export const FIELD_INFO = {
  firstname: {
    description: 'First name (required)',
    required: true,
    example: 'John'
  },
  lastname: {
    description: 'Last name (required)',
    required: true,
    example: 'Smith'
  },
  email: {
    description: 'Email address (optional but recommended)',
    required: false,
    example: 'john.smith@example.com'
  },
  phone: {
    description: 'Phone number (optional)',
    required: false,
    example: '+97412345678'
  },
  address: {
    description: 'Address (optional)',
    required: false,
    example: '123 Main St, Doha'
  },
  rank: {
    description: 'Student/Preceptor rank (required)',
    required: true,
    options: ['spep', 'pharmd', 'both'],
    example: 'pharmd'
  },
  speciality: {
    description: 'Rotation type / Speciality (optional)',
    required: false,
    example: 'Clinical Pharmacy'
  },
  institution: {
    description: 'Institution name (optional)',
    required: false,
    example: 'Hamad Medical Corporation'
  },
  isAdmin: {
    description: 'Admin status (required for preceptors)',
    required: false,
    options: ['true', 'false'],
    example: 'false'
  },
  full_name: {
    description: 'Full name as it appears on QID (optional)',
    required: false,
    example: 'Ahmed Ali Mohammed'
  },
  id_number: {
    description: 'QID number — 11 digits (optional)',
    required: false,
    example: '12345678901'
  },
  id: {
    description: 'Database ID (optional)',
    required: false,
    example: '123'
  },
  id_expiry_date: {
    description: 'QID Expiry Date — dd/mm/yy format (optional)',
    required: false,
    example: '31/12/25'
  },
  iban: {
    description: 'IBAN number — up to 34 alphanumeric characters (optional)',
    required: false,
    example: 'QA58DOHB00001234567890ABCDEFG'
  },
  status: {
    description: 'Student status (optional)',
    required: false,
    example: 'active'
  },
  nationality: {
    description: 'Nationality (optional)',
    required: false,
    example: 'Qatar'
  }
};

/**
 * Generates a clean CSV template using human-readable display headers
 */
export const generateCleanTemplate = (templateType) => {
  const config = TEMPLATE_CONFIGS[templateType];
  if (!config) {
    throw new Error(`Unknown template type: ${templateType}`);
  }

  const lines = [];
  lines.push(config.displayHeaders.join(','));

  if (config.examples && config.examples.length > 0) {
    lines.push(config.examples[0].join(','));
  }

  return lines.join('\n');
};

/**
 * Downloads a clean CSV template
 */
export const downloadCleanTemplate = (templateType) => {
  const config = TEMPLATE_CONFIGS[templateType];
  if (!config) {
    throw new Error(`Unknown template type: ${templateType}`);
  }

  const csvContent = generateCleanTemplate(templateType);

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = config.filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Maps uploaded CSV display headers to internal field names.
 * Returns an array of internal field names in the same order as the CSV headers.
 */
const mapHeaders = (rawHeaders) =>
  rawHeaders.map(h => HEADER_TO_FIELD[h.trim()] || h.trim());

/**
 * Simple CSV validation — supports both display headers and legacy internal names.
 */
export const validateCSVData = (csvContent, templateType) => {
  const config = TEMPLATE_CONFIGS[templateType];
  if (!config) {
    throw new Error(`Unknown template type: ${templateType}`);
  }

  const { headers: rawHeaders, rows: dataRows } = parseCsvContent(csvContent);

  if (rawHeaders.length === 0) {
    throw new Error('CSV file is empty');
  }

  const headers = mapHeaders(rawHeaders); // internal field names

  // Require that every mandatory internal field is covered
  const requiredFields = config.fields.filter(f => FIELD_INFO[f]?.required);
  const missingFields = requiredFields.filter(f => !headers.includes(f));
  if (missingFields.length > 0) {
    // Build a human-readable error using the display header names where possible
    const fieldToDisplay = Object.fromEntries(
      Object.entries(HEADER_TO_FIELD).map(([k, v]) => [v, k])
    );
    const missingLabels = missingFields.map(f => fieldToDisplay[f] || f);
    throw new Error(`Missing required columns: ${missingLabels.join(', ')}`);
  }

  const results = {
    valid: [],
    invalid: [],
    summary: {
      total: dataRows.length,
      valid: 0,
      invalid: 0,
      errors: []
    }
  };

  dataRows.forEach((values, index) => {
    const rowData = {};

    headers.forEach((field, i) => {
      rowData[field] = values[i] || '';
    });

    const rowErrors = [];
    const fieldsToValidate = config.fields.filter(f => FIELD_INFO[f]);

    fieldsToValidate.forEach(field => {
      const info = FIELD_INFO[field];
      if (!info) return;

      if (info.required && (!rowData[field] || rowData[field].trim() === '')) {
        rowErrors.push(`${field} is required`);
      }

      if (field === 'rank' && rowData[field]) {
        if (!info.options.includes(rowData[field].toLowerCase())) {
          rowErrors.push(`rank must be one of: ${info.options.join(', ')}`);
        }
      }

      if (field === 'isAdmin' && rowData[field]) {
        if (!['true', 'false'].includes(rowData[field].toLowerCase())) {
          rowErrors.push(`isAdmin must be 'true' or 'false'`);
        }
      }
    });

    const rowNumber = index + 2;

    if (rowErrors.length === 0) {
      results.valid.push(rowData);
      results.summary.valid++;
    } else {
      results.invalid.push({ row: rowNumber, data: rowData, errors: rowErrors });
      results.summary.invalid++;
      results.summary.errors.push(`Row ${rowNumber}: ${rowErrors.join(', ')}`);
    }
  });

  return results;
};

/**
 * Remaps an uploaded CSV from display headers to backend field names and
 * returns a new Blob ready to be posted to the API.
 *
 * For preceptors it also injects isAdmin=false when the column is absent
 * (new template no longer includes it; backend defaults to non-admin).
 */
export const remapCSVForBackend = (csvContent, templateType) => {
  const { headers: rawHeaders, rows } = parseCsvContent(csvContent);

  if (rawHeaders.length === 0) return new Blob([''], { type: 'text/csv;charset=utf-8;' });
  const internalHeaders = mapHeaders(rawHeaders);

  const needIsAdmin = templateType === 'preceptors' && !internalHeaders.includes('isAdmin');
  const outputHeaders = needIsAdmin ? [...internalHeaders, 'isAdmin'] : internalHeaders;

  const outputLines = [outputHeaders.map(csvEscape).join(',')];

  rows.forEach((values) => {
    const row = needIsAdmin ? [...values, 'false'] : values;
    outputLines.push(row.map(csvEscape).join(','));
  });

  return new Blob([outputLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
};
